import { useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { KIND_OPTIONS, STARTERS, applyStarter, hasLabel } from "@/lib/jobTypes";

const TypeRow = ({ item, i, onChange, onRemove, canRemove }) => (
  <div className="grid grid-cols-[1fr_120px_auto] gap-2 items-center" data-testid={`job-type-row-${i}`}>
    <Input value={item.label} maxLength={40} onChange={(e) => onChange({ label: e.target.value })} placeholder="Job type name" data-testid={`job-type-label-${i}`} />
    <Select value={item.kind} onValueChange={(kind) => onChange({ kind })}>
      <SelectTrigger data-testid={`job-type-kind-${i}`}><SelectValue /></SelectTrigger>
      <SelectContent>{KIND_OPTIONS.map((k) => <SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}</SelectContent>
    </Select>
    <button type="button" onClick={onRemove} disabled={!canRemove} aria-label="Remove job type" data-testid={`job-type-remove-${i}`}
      className="w-8 h-8 inline-flex items-center justify-center rounded-full text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-30 disabled:pointer-events-none">
      <X className="w-4 h-4" />
    </button>
  </div>
);

// Draft editor for a company's job types (shared by the setup wizard and the job types dialog).
export const JobTypesEditor = ({ items, onChange }) => {
  const [v, setV] = useState("");
  const setAt = (i, patch) => onChange(items.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const add = () => {
    const label = v.trim();
    if (!label) return;
    if (hasLabel(items, label)) { toast.error(`“${label}” is already in the list`); return; }
    if (items.length >= 30) { toast.error("You can have up to 30 job types"); return; }
    onChange([...items, { label, kind: "standard" }]);
    setV("");
  };
  return (
    <div className="space-y-4" data-testid="job-types-editor">
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">Start from a list for your industry</Label>
        <Select value="" onValueChange={(k) => onChange(applyStarter(items, STARTERS.find((s) => s.key === k)))}>
          <SelectTrigger data-testid="job-type-starter-select"><SelectValue placeholder="Pick your industry" /></SelectTrigger>
          <SelectContent>{STARTERS.map((s) => <SelectItem key={s.key} value={s.key} data-testid={`job-type-starter-${s.key}`}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <div className="grid grid-cols-[1fr_120px_auto] gap-2 text-xs text-muted-foreground"><span>Name</span><span>Works like</span><span className="w-8" /></div>
        {items.map((item, i) => (
          <TypeRow key={item.id || `new-${i}`} item={item} i={i} canRemove={items.length > 1}
            onChange={(patch) => setAt(i, patch)} onRemove={() => onChange(items.filter((_, k) => k !== i))} />
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={v} maxLength={40} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add a job type" data-testid="job-type-new-input" />
        <Button type="button" variant="outline" onClick={add} className="rounded-full shrink-0" data-testid="job-type-add-btn"><Plus className="w-4 h-4" /></Button>
      </div>
      <ul className="text-xs text-muted-foreground space-y-0.5" data-testid="job-type-kind-help">
        {KIND_OPTIONS.map((k) => <li key={k.v}><span className="font-medium text-foreground">{k.label}</span>: {k.hint}</li>)}
      </ul>
    </div>
  );
};
